import React from 'react';
import {
  Star,
  Receipt,
  Users,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react';
import { Badge } from '../ui/badge';

const getMealBadgeClass = (mealType) => {
  switch (mealType) {
    case 'breakfast': return 'meal-breakfast';
    case 'brunch': return 'meal-brunch';
    case 'lunch': return 'meal-lunch';
    case 'dinner': return 'meal-dinner';
    case 'drinks': return 'meal-drinks';
    default: return 'meal-other';
  }
};

const VisitCard = ({ visit, onEdit, onDelete }) => {
  return (
    <div className="visit-timeline-item" data-testid={`visit-${visit.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[#1A1918]">
                {new Date(visit.visited_at).toLocaleDateString('en-ZA', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
              <Badge className={`meal-badge ${getMealBadgeClass(visit.meal_type)}`}>
                {visit.meal_type}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onEdit(visit)}
                className="text-xs text-[#9A9590] hover:text-[#A94438] transition-colors flex items-center gap-1"
                data-testid={`edit-visit-${visit.id}`}
              >
                <PencilSimple weight="bold" className="w-3.5 h-3.5" />
                Edit
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Delete this visit? This cannot be undone.')) {
                    onDelete(visit.id);
                  }
                }}
                className="text-xs text-[#9A9590] hover:text-[#A94438] transition-colors flex items-center gap-1"
                data-testid={`delete-visit-${visit.id}`}
              >
                <Trash weight="bold" className="w-3.5 h-3.5" />
                Delete
              </button>
            </div>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-1 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                weight={star <= visit.overall_rating ? 'fill' : 'regular'}
                className={`w-4 h-4 ${star <= visit.overall_rating ? 'text-[#A94438]' : 'text-[#E8DCD0]'}`}
              />
            ))}
            <span className="text-sm text-[#7A7873] ml-2">
              {visit.overall_rating}/5
            </span>
          </div>

          {/* Notes */}
          {visit.notes && (
            <p className="text-sm text-[#7A7873] mb-2">{visit.notes}</p>
          )}

          {/* Journal */}
          {visit.journal_entry && (
            <p className="text-sm text-[#4A4845] italic mb-2 border-l-2 border-[#C4A35A] pl-2">{visit.journal_entry}</p>
          )}

          {/* Vibe + Companions */}
          {(visit.vibe || (visit.companions && visit.companions.length > 0)) && (
            <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
              {visit.vibe && (
                <span className="bg-[#F5E6C8]/60 text-[#8B6914] px-2 py-0.5 rounded-full capitalize">{visit.vibe}</span>
              )}
              {visit.companions?.length > 0 && (
                <span className="text-[#7A7873]">with {visit.companions.join(', ')}</span>
              )}
            </div>
          )}

          {/* Dishes */}
          {visit.dishes_ordered && visit.dishes_ordered.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {visit.dishes_ordered.map((dish, i) => (
                <span key={i} className="text-xs bg-[#E8DCD0]/50 px-2 py-0.5 rounded">
                  {dish}
                </span>
              ))}
            </div>
          )}

          {/* Spend */}
          {visit.bill_total && (
            <div className="flex items-center gap-4 text-sm text-[#7A7873]">
              <div className="flex items-center gap-1">
                <Receipt weight="duotone" className="w-4 h-4" />
                <span>R{visit.bill_total.toLocaleString()}</span>
              </div>
              {visit.party_size && (
                <div className="flex items-center gap-1">
                  <Users weight="duotone" className="w-4 h-4" />
                  <span>{visit.party_size} people</span>
                </div>
              )}
              {visit.spend_per_person && (
                <span className="text-[#4B5945]">
                  R{visit.spend_per_person.toLocaleString()}/person
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisitCard;
